// frontend/src/components/SmartPicker.js
import { useState, useEffect, useRef } from 'react';
import api from '../api';

const S = {
  wrap: { position: 'relative', width: '100%' },
  input: {
    background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,18%)',
    padding: '9px 12px', color: 'hsl(0,0%,90%)',
    fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
    outline: 'none', width: '100%', boxSizing: 'border-box',
  },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
    background: 'hsl(0,0%,6%)', border: '1px solid hsl(0,0%,20%)',
    borderTop: 'none', maxHeight: 220, overflowY: 'auto',
  },
  option: {
    padding: '8px 12px', cursor: 'pointer',
    fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
    color: 'hsl(0,0%,80%)', borderBottom: '1px solid hsl(0,0%,10%)',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  chip: {
    fontSize: 8, padding: '1px 5px', textTransform: 'uppercase',
    letterSpacing: '0.08em', border: '1px solid hsl(0,0%,25%)',
    color: 'hsl(0,0%,50%)',
  },
  empty: {
    padding: '10px 12px', fontFamily: "'JetBrains Mono', monospace",
    fontSize: 10, color: 'hsl(0,0%,35%)',
  },
};

/**
 * SmartPicker — searchable dropdown backed by an API endpoint.
 *
 * Props:
 *   endpoint   string      API path, e.g. '/orders'
 *   params     object      Query params for filtering, e.g. { status: 'confirmed' }
 *   labelKey   string      Field to show as primary label, e.g. 'order_no'
 *   searchKeys string[]    Fields to search across, e.g. ['order_no', 'customer_name']
 *   onSelect   function    Called with the full record object on selection
 *   placeholder string
 *   value      string      Controlled display value (shown in input after selection)
 *   renderOption function  Optional: (record) => ReactNode for custom row content
 *   NOTE: Options are fetched once on first open and cached for the component's lifetime.
 *         If `params` needs to change at runtime, unmount and remount the picker.
 */
export default function SmartPicker({
  endpoint, params = {}, labelKey, searchKeys = [],
  onSelect, placeholder = 'Search...', value = '', renderOption,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  // Fetch options once on open
  useEffect(() => {
    if (!open || options.length > 0) return;
    setLoading(true);
    api.get(endpoint, { params })
      .then(r => setOptions(r.data || []))
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = query.trim()
    ? options.filter(opt =>
        searchKeys.some(k => String(opt[k] || '').toLowerCase().includes(query.toLowerCase()))
      )
    : options;

  const handleSelect = (record) => {
    setOpen(false);
    setQuery('');
    onSelect(record);
  };

  return (
    <div style={S.wrap} ref={wrapRef}>
      <input
        style={S.input}
        placeholder={placeholder}
        value={open ? query : value}
        onFocus={() => setOpen(true)}
        onChange={e => setQuery(e.target.value)}
        data-testid="smart-picker-input"
      />
      {open && (
        <div style={S.dropdown} data-testid="smart-picker-dropdown">
          {loading && <div style={S.empty}>Loading...</div>}
          {!loading && filtered.length === 0 && <div style={S.empty}>No results</div>}
          {!loading && filtered.map(opt => (
            <div
              key={opt.id ?? opt._id ?? opt[labelKey]}
              style={S.option}
              onClick={() => handleSelect(opt)}
              onMouseEnter={e => e.currentTarget.style.background = 'hsl(0,0%,10%)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              data-testid="smart-picker-option"
            >
              {renderOption ? renderOption(opt) : (
                <>
                  <span>{opt[labelKey]}</span>
                  {opt.status && <span style={S.chip}>{opt.status.replace(/_/g, ' ')}</span>}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
